import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const HEADER = 'var(--header-height, 0px)';

/** Sticky media band: viewport below the site header (matches `stickyViewport` height). */
const BAND = `calc(100dvh - ${HEADER})`;
const BAND_IMP = `${BAND} !important`;

/** Portrait band: iframe wider than viewport so ~16:9 fills height (`data-kl-cf015-layer="mobile"`). */
const MOBILE_COVER_WIDTH = `max(100%, calc((16 / 9) * (100dvh - ${HEADER})))`;

/**
 * Wide band: iframe at least as tall as 16:9 at full width, and at least ~5:4 at full width (common oEmbed).
 * Centers vertically so letterboxing is cropped (Vimeo + tall/narrow sources on desktop).
 */
const DESKTOP_COVER_IFRAME_HEIGHT = `max(${BAND}, calc((9 / 16) * 100vw), calc(100vw / 1.25))`;

const cf015AmbVideoBase = {
  alignSelf: 'stretch',
  aspectRatio: 'auto !important',
  flex: '1 1 auto',
  height: BAND_IMP,
  maxHeight: 'none',
  minHeight: BAND_IMP,
  minWidth: '100%',
  overflow: 'hidden',
  position: 'relative',
  width: '100% !important',
};

const cf015IframeMobile = {
  border: 'none',
  boxSizing: 'border-box',
  height: BAND_IMP,
  left: '50% !important',
  margin: '0 !important',
  maxWidth: 'none',
  pointerEvents: 'none',
  position: 'absolute',
  top: '0 !important',
  transform: 'translate(-50%, 0) !important',
  width: `${MOBILE_COVER_WIDTH} !important`,
};

const cf015IframeDesktop = {
  border: 'none',
  boxSizing: 'border-box',
  height: `${DESKTOP_COVER_IFRAME_HEIGHT} !important`,
  left: '0 !important',
  margin: '0 !important',
  maxWidth: 'none',
  minHeight: BAND_IMP,
  pointerEvents: 'none',
  position: 'absolute',
  top: '50% !important',
  transform: 'translate(0, -50%) !important',
  width: '100% !important',
};

/** Image root under `mediaLayer` (Video root gets the same; harmless if no `img`). */
const cf015DesktopContentRoot = {
  alignSelf: 'stretch',
  flex: 1,
  minHeight: BAND,
  position: 'relative',
  width: '100%',
};

const cf015DesktopImageLink = {
  display: 'block',
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
};

const cf015DesktopImg = {
  height: BAND_IMP,
  left: 0,
  objectFit: 'cover',
  objectPosition: 'center',
  position: 'absolute',
  top: 0,
  width: '100%',
};

const helpers = createMultiStyleConfigHelpers([
  'section',
  'stickyViewport',
  'mediaLayer',
  'textStack',
  'step',
  'stepInner',
  'heading',
  'body'
]);

/** CF-015: sticky media band, scroll-fade steps, desktop vs mobile video (`data-kl-cf015-layer`). */
export const mediaWithScrollableTextStyles = helpers.defineMultiStyleConfig({
  baseStyle: {
    section: {
      position: 'relative',
      width: '100%',
    },
    stickyViewport: {
      position: 'sticky',
      top: HEADER,
      height: BAND,
      minHeight: BAND,
      width: '100%',
      overflow: 'hidden',
      sx: {
        '& [data-kl-cf015-layer] .amb-video': cf015AmbVideoBase,
        // KINDW-1387: hide native HTML5 controls if a file URL video ever slips through with controls on
        '& [data-kl-cf015-layer] .amb-video video::-webkit-media-controls': {display: 'none'},
        '& [data-kl-cf015-layer] .amb-video video::-webkit-media-controls-enclosure': {display: 'none'},
        '& [data-kl-cf015-layer="mobile"] .amb-video iframe': cf015IframeMobile,
        '& [data-kl-cf015-layer="desktop"] .amb-video iframe': cf015IframeDesktop,
        '& [data-kl-cf015-layer="desktop"] > div': {minHeight: BAND},
        '& [data-kl-cf015-layer="desktop"] > div > div': cf015DesktopContentRoot,
        '& [data-kl-cf015-layer="desktop"] > div > div a': cf015DesktopImageLink,
        '& [data-kl-cf015-layer="desktop"] > div > div img': cf015DesktopImg,
      },
    },
    mediaLayer: {
      'position': 'absolute',
      'inset': 0,
      'zIndex': 0,
      'display': 'flex',
      'flexDirection': 'column',
      '& > div': {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      },
      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
    },
    textStack: {
      'position': 'relative',
      'zIndex': 1,
      'marginTop': `calc(${HEADER} - 100dvh)`,
      'pointerEvents': 'none',
      '& a, & button': {
        pointerEvents: 'auto',
      },
    },
    // Padding on `stepInner`: absolute fade layer sits inside `step` and ignores `step` horizontal padding.
    step: {
      minHeight: BAND,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 10,
    },
    stepInner: {
      maxW: 'container.md',
      width: '100%',
      textAlign: 'center',
      color: 'white',
      textShadow: '0 1px 24px rgba(0,0,0,0.45)',
      px: {base: '20px', md: 12, lg: 24},
      boxSizing: 'border-box',
    },
    heading: {
      color: {base: 'white', md: 'whiteAlpha.600'},
      fontFamily: 'aktiv-grotesk, sans-serif',
      fontWeight: 500,
      fontSize: {base: '12px', md: '14px'},
      lineHeight: 1.2,
      letterSpacing: '1.4px',
      mb: {base: '12px', md: '18px'},
      textTransform: 'uppercase',
    },
    body: {
      fontFamily: 'aktiv-grotesk, sans-serif',
      fontWeight: 400,
      fontSize: {base: '12px', md: '14px'},
      lineHeight: 1.5,
      sx: {
        '& .rich-text-content h2': {
          fontSize: {base: '28px', md: '42px'},
          fontWeight: 500,
          lineHeight: 1.2,
        },
        '& .rich-text-content p': {
          fontSize: {base: '16px', md: '20px'},
        },
      },
    },
  },
});
